'use client';

import { c as _c } from "react/compiler-runtime";
import type { PropsWithStyles } from '@morpho-org/ui-v6/types';
import { cn } from '@morpho-org/ui-v6/utils';
import { Icon } from '@repo/ui';
import { Close, Search as SearchIcon } from '@repo/ui/icons';
import { cva } from 'class-variance-authority';
import { type ForwardedRef, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import useGlobalSearchShortcut from '../../hooks/useGlobalSearchShortcut';
export type SearchSize = 'default' | 'large';
export interface SearchProps extends PropsWithStyles {
  disabled?: boolean;
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  width?: number | string;
  fullWidth?: boolean;
  isGlobalSearchInput?: boolean;
  debounceTime?: number;
  size?: SearchSize;
  autoFocus?: boolean;
  'data-testid'?: string;
}

/**
 * Search root variants - Tailwind replica of dapp-ui-v5 SearchRoot.
 */
const searchRootVariants = cva([
// Layout
'flex items-center',
// Visual
'bg-muted bg-clip-padding', 'outline-[0.5px] outline-transparent', 'will-change-[background-color,outline-color]',
// Interaction
'hover:bg-accent hover:not-focus-within:outline-accent', 'focus-within:bg-accent focus-within:outline-ring'], {
  variants: {
    size: {
      default: 'h-[26px] gap-[2px] rounded-[100px] py-[3px] pr-[10px] pl-[5px]',
      large: 'px-sm h-[41px] gap-2xs rounded-lg py-[10px]'
    },
    disabled: {
      true: 'cursor-not-allowed opacity-50'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});

/**
 * Search input variants - Tailwind replica of dapp-ui-v5 SearchInput.
 */
const searchInputVariants = cva(['w-full border-none bg-transparent', 'text-foreground truncate', 'placeholder:text-muted-foreground placeholder:leading-[inherit]', 'focus:outline-none', 'disabled:cursor-not-allowed disabled:text-muted-foreground'], {
  variants: {
    size: {
      default: 'text-2xs leading-[normal]',
      large: 'text-xs'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});

/**
 * Clear button styles - Tailwind replica of dapp-ui-v5 ClearButton.
 */
const clearButtonStyles = cn(['size-5 flex cursor-pointer items-center justify-center', 'rounded-full border-none bg-transparent p-[2px]', 'hover:bg-accent', 'focus-visible:outline-ring focus-visible:outline-1']);

/**
 * A search input component with global shortcut support.
 * Tailwind replica of dapp-ui-v5 Search.
 *
 * @param {number | string} props.width - The width of the search input.
 * @param {boolean} props.fullWidth - Whether the search input should take the full width of its container.
 * @param {boolean} props.isGlobalSearchInput - Whether the search input is the global search input (used for keyboard shortcut). If true, the shortcut hint will be shown.
 * @param {number} props.debounceTime - The debounce delay in milliseconds for the onChange callback. Default is 300ms. If 0, debounce is skipped.
 */
const Search = (t0, ref) => {
  const $ = _c(64);
  let autoFocus;
  let className;
  let disabled;
  let fullWidth;
  let onChange;
  let placeholder;
  let props;
  let t1;
  let t2;
  let t3;
  let value;
  let width;
  if ($[0] !== t0) {
    ({
      className,
      value,
      placeholder,
      onChange,
      disabled,
      width,
      fullWidth,
      isGlobalSearchInput: t1,
      debounceTime: t2,
      size: t3,
      autoFocus,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = autoFocus;
    $[2] = className;
    $[3] = disabled;
    $[4] = fullWidth;
    $[5] = onChange;
    $[6] = placeholder;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = value;
    $[12] = width;
  } else {
    autoFocus = $[1];
    className = $[2];
    disabled = $[3];
    fullWidth = $[4];
    onChange = $[5];
    placeholder = $[6];
    props = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    value = $[11];
    width = $[12];
  }
  const isGlobalSearchInput = t1 === undefined ? false : t1;
  const debounceTime = t2 === undefined ? 300 : t2;
  const size = t3 === undefined ? "default" : t3;
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState(value || "");
  const [isInputFocused, setIsInputFocused] = useState(false);
  let t4;
  if ($[13] !== onChange) {
    t4 = value_0 => {
      onChange?.(value_0);
    };
    $[13] = onChange;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const debouncedCallback = useDebouncedCallback(t4, debounceTime);
  let t5;
  if ($[15] !== debounceTime || $[16] !== debouncedCallback || $[17] !== onChange) {
    t5 = newValue => {
      setLocalValue(newValue);
      if (debounceTime > 0) {
        debouncedCallback(newValue);
      } else {
        onChange?.(newValue);
      }
    };
    $[15] = debounceTime;
    $[16] = debouncedCallback;
    $[17] = onChange;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const handleChange = t5;
  let t6;
  if ($[19] !== debouncedCallback || $[20] !== onChange) {
    t6 = () => {
      setLocalValue("");
      debouncedCallback.cancel();
      onChange?.("");
    };
    $[19] = debouncedCallback;
    $[20] = onChange;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const handleClear = t6;
  let t7;
  let t8;
  if ($[22] !== value) {
    t7 = () => {
      setLocalValue(value || "");
    };
    t8 = [value];
    $[22] = value;
    $[23] = t7;
    $[24] = t8;
  } else {
    t7 = $[23];
    t8 = $[24];
  }
  useEffect(t7, t8);
  useGlobalSearchShortcut(inputRef, isGlobalSearchInput);
  let t9;
  if ($[25] !== className || $[26] !== disabled || $[27] !== fullWidth || $[28] !== size || $[29] !== width) {
    t9 = cn(searchRootVariants({
      size,
      disabled: Boolean(disabled)
    }), fullWidth ? "w-full" : !width && "w-[200px]", className);
    $[25] = className;
    $[26] = disabled;
    $[27] = fullWidth;
    $[28] = size;
    $[29] = width;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== fullWidth || $[32] !== width) {
    t10 = !fullWidth && width ? {
      width
    } : undefined;
    $[31] = fullWidth;
    $[32] = width;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <SearchIcon />;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const t12 = isInputFocused || localValue ? "text-primary" : "text-secondary-foreground";
  let t13;
  if ($[35] !== t12) {
    t13 = cn("size-5", t12);
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t13) {
    t14 = <Icon icon={t11} className={t13} />;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== handleChange) {
    t15 = e => handleChange(e.target.value);
    $[39] = handleChange;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setIsInputFocused(true);
    t17 = () => setIsInputFocused(false);
    $[41] = t16;
    $[42] = t17;
  } else {
    t16 = $[41];
    t17 = $[42];
  }
  let t18;
  if ($[43] !== size) {
    t18 = searchInputVariants({
      size
    });
    $[43] = size;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== autoFocus || $[46] !== disabled || $[47] !== localValue || $[48] !== placeholder || $[49] !== props || $[50] !== t15 || $[51] !== t18) {
    t19 = <input {...props} ref={inputRef} value={localValue} placeholder={placeholder} onChange={t15} disabled={disabled} autoFocus={autoFocus} onFocus={t16} onBlur={t17} className={t18} />;
    $[45] = autoFocus;
    $[46] = disabled;
    $[47] = localValue;
    $[48] = placeholder;
    $[49] = props;
    $[50] = t15;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== disabled || $[54] !== handleClear || $[55] !== localValue) {
    t20 = localValue && !disabled && <button type="button" onClick={handleClear} aria-label="Clear search" className={clearButtonStyles}><Icon icon={<Close />} className="text-foreground size-5" /></button>;
    $[53] = disabled;
    $[54] = handleClear;
    $[55] = localValue;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== ref || $[58] !== t10 || $[59] !== t14 || $[60] !== t19 || $[61] !== t20 || $[62] !== t9) {
    t21 = <div ref={ref} className={t9} style={t10}>{t14}{t19}{t20}</div>;
    $[57] = ref;
    $[58] = t10;
    $[59] = t14;
    $[60] = t19;
    $[61] = t20;
    $[62] = t9;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  return t21;
};
const ForwardedSearch = forwardRef(Search);
ForwardedSearch.displayName = 'Search';
export { ForwardedSearch as Search };